import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import moment from 'moment'
import * as fromShopStore from '@app/shop/store'
import { CloseModal } from '@app/modals/store/actions/layout.actions'
import { ShopInvoiceForm, ShopInvoicePayload } from '@app/shop/models/shopOrderAccount.model'
import { environment } from 'environments/environment'
import { popupService } from '@app/shared/services/popup.service'
import { ShopOrderService } from '@app/shop/services/shopOrder.service'
import { ToastHelper } from '@app/core/services/toast.service'

interface EditableLine {
  qbLineId?: string
  serviceIds?: string[]
  itemId: string
  description: string
  // Quantity and rate (dollars); when both are set the amount is qty x rate
  qty: string | number
  rate: string | number
  // Dollars as typed (number input gives a number, or null when cleared); converted to cents on save
  amount: string | number
  // Services added to the order since the invoice was created (edit only); highlighted. With a qbLineId: an
  // existing labor line they were merged into (same rate)
  isNew?: boolean
  // How many of its services were deleted from the order after invoicing (edit only). All of them: the line
  // is dropped on save (red); some: it stays and its amount needs a look (amber)
  deletedServices?: number
  // How many of its services changed on the order since the invoice was saved (edit only): the line comes
  // drafted again from them, highlighted like new lines
  changedServices?: number
  // Bills only labor services (from the Backend)
  labor?: boolean
}

// "$1,200.50" -> 1200.5; empty -> null; anything else that isn't a number -> NaN
const parseNumber = (value: string | number): number => {
  if (value === '' || value === null || value === undefined) {
    return null
  }
  return Number(String(value).replace(/[$,\s]/g, ''))
}

/**
 * Create or edit a shop order's QuickBooks invoice (shop QuickBooks connection, not the freight one).
 * props: { shopOrderId: string, accountId?: string, canUpdate: boolean }
 * Without accountId the form starts from the order's not yet invoiced services.
 */
@Component({
  selector: 'app-shop-invoice-modal',
  templateUrl: './shop-invoice-modal.component.html',
  styleUrls: ['./shop-invoice-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ShopInvoiceModalComponent implements OnInit, OnDestroy {
  @Input()
  props: { shopOrderId: string, accountId?: string, canUpdate?: boolean }

  private destroyed$ = new Subject<boolean>()

  // The form is loaded here, not kept in the store: each opening fetches its own copy, so nothing left
  // from an earlier opening (or another invoice) can show up in this one
  loading$ = new BehaviorSubject<boolean>(true)
  saving$: Observable<boolean>
  form: ShopInvoiceForm
  lines: EditableLine[] = []
  salesTermId: string = null
  // app-date's parent object; dueDate as YYYY-MM-DD, empty to leave it to the terms
  due: { dueDate: string } = { dueDate: '' }
  billEmail = ''
  // The customer's emails, shaped for app-select
  emailOptions: { value: string }[] = []
  customerMemo = ''
  savePressed = false
  // The save in progress is a Save and Send (its button shows the spinner)
  sending = false
  qbUrl = environment.qbUrl

  constructor(
    private store: Store<fromShopStore.State>,
    private cdr: ChangeDetectorRef,
    private popupService: popupService,
    private shopOrderService: ShopOrderService,
    private notify: ToastHelper
  ) {}

  get isEdit() {
    return !!this.props?.accountId
  }

  get readOnly() {
    return !this.props?.canUpdate || this.form?.invoice?.status === 'VOIDED' || !!this.form?.disconnected
  }

  get hasNewLines() {
    return this.lines.some(line => this.isBlue(line))
  }

  // Some of its services were deleted from the order, some are left: redrafted from those (yellow)
  isYellow(line: EditableLine) {
    return !!line.deletedServices && !this.isDroppedOnSave(line)
  }

  // Added or changed since invoicing. A yellow line that also got labor added stays yellow only: one color,
  // one message (the yellow one says it)
  isBlue(line: EditableLine) {
    return (!!line.isNew || !!line.changedServices) && !this.isYellow(line) && !this.isDroppedOnSave(line)
  }

  // A yellow line that labor added since joined
  get hasYellowWithNewLabor() {
    return this.lines.some(line => this.isYellow(line) && line.isNew)
  }

  /**
   * The blue lines' hint, naming what was added: parts, labor, or labor merged into an existing line of
   * the same rate. A new line on the labor item counts as labor
   */
  get newLinesHint() {
    const added = this.lines.filter(line => this.isBlue(line) && line.isNew && !line.qbLineId)
    const isLabor = (line: EditableLine) => this.isLaborLine(line)
    const parts = added.some(line => !isLabor(line))
    const labor = added.some(isLabor)
    const merged = this.lines.some(line => this.isBlue(line) && line.isNew && line.qbLineId)
    const changed = this.lines.some(line => this.isBlue(line) && line.changedServices)
    const what = parts && (labor || merged) ? 'Parts and labor' : parts ? 'Parts' : 'Labor'
    const mergeNote = merged ? (parts || labor ? '; labor at an existing rate joins that line' : ', merged into the line of the same rate') : ''
    const sentences = [
      ...(added.length || merged ? [`${what} added to the order since invoicing${mergeNote}`] : []),
      ...(changed ? ['lines updated from services changed on the order since'] : [])
    ]
    const text = sentences.join('; ')
    return `Blue: ${text.charAt(0).toUpperCase() + text.slice(1)}. Saving writes ${parts || labor || changed ? 'them' : 'it'} to the invoice.`
  }

  // Every line is red: nothing is left to bill, the invoice should be deleted rather than saved
  get allLinesDropped() {
    return this.lines.length > 0 && !this.savedLines.length
  }

  get hasDeletedLines() {
    return this.lines.some(line => this.isDroppedOnSave(line))
  }

  get hasPartlyDeletedLines() {
    return this.lines.some(line => this.isYellow(line))
  }

  // Every service it billed was deleted from the order: saving removes it from the invoice
  isDroppedOnSave(line: EditableLine) {
    return !!line.deletedServices && !line.serviceIds?.length
  }

  // The lines saving sends
  private get savedLines() {
    return this.lines.filter(line => !this.isDroppedOnSave(line))
  }

  // Payments applied: no delete (the backend refuses too)
  get isPaid() {
    return this.form?.invoice?.status === 'PAID' || this.form?.invoice?.status === 'PARTIALLY_PAID'
  }

  get total() {
    return this.savedLines.reduce((sum, line) => sum + (parseNumber(line.amount) || 0), 0)
  }

  // What the modal holds, to tell whether closing loses changes
  private initialState = ''
  private get state() {
    return JSON.stringify({ lines: this.lines, salesTermId: this.salesTermId, due: this.due, billEmail: this.billEmail, customerMemo: this.customerMemo })
  }

  ngOnInit() {
    this.saving$ = this.store.select(fromShopStore.selectShopInvoiceCreating)
    this.shopOrderService
      .getInvoiceForm(this.props.shopOrderId, this.props.accountId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: resp => this.setForm(resp.data),
        error: err => {
          this.notify.error(err?.message || err)
          this.close()
        }
      })
  }

  private setForm(form: ShopInvoiceForm) {
    this.form = form
    this.lines = (form.invoice?.lines || []).map(line => ({
      qbLineId: line.qbLineId,
      serviceIds: line.serviceIds || [],
      itemId: line.itemId,
      description: line.description || '',
      qty: line.qty ?? '',
      rate: line.unitPrice !== undefined && line.unitPrice !== null ? (line.unitPrice / 100).toFixed(2) : '',
      amount: ((line.amount || 0) / 100).toFixed(2),
      isNew: !!line.isNew,
      deletedServices: line.deletedServices || 0,
      changedServices: line.changedServices || 0,
      labor: !!line.labor
    }))
    this.salesTermId = form.invoice?.salesTermId || null
    this.due = { dueDate: form.invoice?.dueDate || '' }
    this.billEmail = form.invoice?.billEmail || ''
    this.emailOptions = (form.customer?.emails || []).map(value => ({ value }))
    this.customerMemo = form.invoice?.customerMemo || ''
    // Labor added to the order since the invoice was saved changes the hours the shop fee bills; done before
    // the snapshot below, as part of what opening shows, not an edit to confirm on close
    this.syncShopFee()
    this.initialState = this.state
    this.loading$.next(false)
    this.cdr.markForCheck()
  }

  // Like QuickBooks: picking a "Net N" term sets the due date to the invoice date + N days.
  // Like freight invoices: offer to make the picked term the customer's default.
  async setSalesTerm(termId: string) {
    this.salesTermId = termId || null
    const term = this.form?.terms?.find(t => t.Id === termId)
    // "Net N": the date it gives; no term or a date-driven one: no date of our own, QuickBooks works it out
    this.due = {
      dueDate: term?.DueDays !== undefined ? moment(this.form.invoice?.txnDate || undefined).add(term.DueDays, 'days').format('YYYY-MM-DD') : ''
    }
    this.cdr.markForCheck()
    if (!term || this.readOnly || term.Id === this.form.customer?.defaultTermId) {
      return
    }
    if (!(await this.popupService.confirm(`Do you want to set Default Customer Terms to ${term.Name}?`))) {
      return
    }
    this.shopOrderService.setCustomerTerm(this.props.shopOrderId, term.Id).pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.form = { ...this.form, customer: { ...this.form.customer, defaultTermId: term.Id } }
        this.notify.ok(`Default terms of ${this.form.customer?.name || 'the customer'} set to ${term.Name}`)
        this.cdr.markForCheck()
      },
      error: err => this.notify.error(err?.message || err)
    })
  }

  setDueDate(dueDate: string) {
    this.due = { dueDate: dueDate ? dueDate.substring(0, 10) : '' }
  }

  private itemById(itemId: string) {
    return (this.form?.items || []).find(i => i.Id === itemId)
  }

  /**
   * Like QuickBooks: picking an item fills the line's description and rate from it, unless they were typed
   * (still empty, or still the previous item's)
   */
  onItemChange(line: EditableLine, itemId: string) {
    const prev = this.itemById(line.itemId)
    const next = this.itemById(itemId)
    line.itemId = itemId
    if (!next) {
      return
    }
    if (!line.description?.trim() || line.description === prev?.Description) {
      line.description = next.Description || ''
    }
    const rate = parseNumber(line.rate)
    if (next.UnitPrice && (rate === null || (prev && Math.round(rate * 100) === prev.UnitPrice))) {
      line.rate = (next.UnitPrice / 100).toFixed(2)
      if (parseNumber(line.qty) === null) {
        line.qty = 1
      }
      this.onQtyRateChange(line)
    }
    // A line moved on or off the labor item changes the labor hours
    if (!this.isShopFeeLine(line)) {
      this.syncShopFee()
    }
  }

  // Lines added by hand go before the shop fee, which stays last
  addLine() {
    const line: EditableLine = { itemId: this.form?.defaultItemId || null, description: '', qty: '', rate: '', amount: '' }
    const fee = this.lines.findIndex(l => this.isShopFeeLine(l))
    this.lines = fee < 0 ? [...this.lines, line] : [...this.lines.slice(0, fee), line, ...this.lines.slice(fee)]
  }

  removeLine(index: number) {
    this.lines = this.lines.filter((_, i) => i !== index)
    this.syncShopFee()
  }

  isShopFeeLine(line: EditableLine) {
    return !!this.form?.shopFeeItemId && line.itemId === this.form.shopFeeItemId
  }

  // Labor lines are the ones on the labor item; their hours are the shop fee's quantity
  private get laborHours() {
    const hours = this.savedLines
      .filter(line => this.isLaborLine(line))
      .reduce((sum, line) => sum + (Number.isFinite(parseNumber(line.qty)) ? parseNumber(line.qty) || 0 : 0), 0)
    return Math.round(hours * 100) / 100
  }

  /**
   * Labor: the Backend says so for lines billing services (the labor and part default items can be the same
   * item); a line added by hand counts as labor on the labor item
   */
  isLaborLine(line: EditableLine) {
    return !!line.labor || (!line.serviceIds?.length && !!this.form?.defaultItemId && line.itemId === this.form.defaultItemId)
  }

  get canAddShopFee() {
    return !!this.form?.shopFeeItemId && !this.readOnly && !this.lines.some(line => this.isShopFeeLine(line))
  }

  shopFeeAdding = false

  /**
   * "+ Shop Fee" (client, 2026-10-15): the shop fee item of the settings as the invoice's last line, its
   * quantity the invoice's labor hours and its price the item's, read from QuickBooks now (the catalog's
   * when QuickBooks can't be reached). Only on the invoice, never a service of the order.
   */
  addShopFee() {
    const itemId = this.form?.shopFeeItemId
    if (!this.canAddShopFee || this.shopFeeAdding) {
      return
    }
    const add = (description: string, unitPrice: number) => {
      const qty = this.laborHours
      const line: EditableLine = {
        itemId,
        description,
        qty,
        rate: ((unitPrice || 0) / 100).toFixed(2),
        amount: ''
      }
      this.onQtyRateChange(line)
      this.lines = [...this.lines, line]
      this.cdr.markForCheck()
    }
    const cached = this.itemById(itemId)
    this.shopFeeAdding = true
    this.shopOrderService.getQuickBooksCatalogItem(itemId).pipe(takeUntil(this.destroyed$)).subscribe({
      next: resp => {
        this.shopFeeAdding = false
        const live = resp?.data
        if (!live || live.active === false) {
          this.notify.error('The shop fee item is no longer in QuickBooks. Pick another one in company settings.')
          this.cdr.markForCheck()
          return
        }
        add(live.description || cached?.Description || live.name || '', live.unitPrice ?? cached?.UnitPrice)
      },
      error: () => {
        this.shopFeeAdding = false
        add(cached?.Description || cached?.Name || '', cached?.UnitPrice)
      }
    })
  }

  // The shop fee's quantity follows the invoice's labor hours while they change in the modal
  private syncShopFee() {
    const fee = this.lines.find(line => this.isShopFeeLine(line))
    if (!fee) {
      return
    }
    const hours = this.laborHours
    if (parseNumber(fee.qty) !== hours) {
      fee.qty = hours
      this.onQtyRateChange(fee)
    }
  }

  // Grouped labor lines list every service; show them all, up to a point
  descriptionRows(line: EditableLine) {
    return Math.min(Math.max((line.description || '').split('\n').length, 2), 10)
  }

  hasQtyRate(line: EditableLine) {
    return parseNumber(line.qty) !== null && parseNumber(line.rate) !== null
  }

  // Keep amount = qty x rate while both are filled in (and are numbers)
  onQtyRateChange(line: EditableLine) {
    const qty = parseNumber(line.qty)
    const rate = parseNumber(line.rate)
    if (this.hasQtyRate(line) && Number.isFinite(qty) && Number.isFinite(rate)) {
      line.amount = (Math.round(qty * rate * 100) / 100).toFixed(2)
    }
    if (!this.isShopFeeLine(line)) {
      this.syncShopFee()
    }
  }

  isQtyValid(line: EditableLine) {
    const qty = parseNumber(line.qty)
    return qty === null || (Number.isFinite(qty) && qty >= 0)
  }

  isRateValid(line: EditableLine) {
    const rate = parseNumber(line.rate)
    return rate === null || Number.isFinite(rate)
  }

  // Why a line can't be saved, or null
  lineError(line: EditableLine) {
    if (!line.itemId) {
      return 'pick an item'
    }
    if (!this.isQtyValid(line)) {
      return 'the quantity must be a number, 0 or more'
    }
    if (!this.isRateValid(line)) {
      return 'the rate must be a number'
    }
    const amount = parseNumber(line.amount)
    return amount === null || !Number.isFinite(amount) ? 'enter an amount' : null
  }

  isLineValid(line: EditableLine) {
    return !this.lineError(line)
  }

  /**
   * @param send Save and Send: after saving, QuickBooks emails the invoice (its PDF and payment link) to
   *   the bill email, and the invoice becomes SENT
   */
  async save(send = false) {
    this.savePressed = true
    const lines = this.savedLines
    if (!lines.length) {
      this.notify.error('An invoice needs at least one line')
      return
    }
    const invalid = this.lines.findIndex(line => !this.isDroppedOnSave(line) && !this.isLineValid(line))
    if (invalid >= 0) {
      this.notify.error(`Line ${invalid + 1}: ${this.lineError(this.lines[invalid])}`)
      document.getElementById(`sim-line-${invalid}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      return
    }
    const email = this.billEmail?.trim()
    if (send && !email) {
      this.notify.error('Add the email to send the invoice to')
      return
    }
    if (send && !(await this.popupService.confirm(`Email the invoice to ${email}?`))) {
      return
    }
    this.sending = send
    const invoice: ShopInvoicePayload = {
      lines: lines.map(line => ({
        ...(line.qbLineId ? { qbLineId: line.qbLineId } : {}),
        ...(line.serviceIds?.length ? { serviceIds: line.serviceIds } : {}),
        itemId: line.itemId,
        description: line.description,
        ...(this.hasQtyRate(line) ? { qty: parseNumber(line.qty), unitPrice: Math.round(parseNumber(line.rate) * 100) } : {}),
        amount: Math.round(parseNumber(line.amount) * 100)
      })),
      salesTermId: this.salesTermId || null,
      dueDate: this.due.dueDate || null,
      billEmail: email || null,
      customerMemo: this.customerMemo || '',
      ...(send ? { send: true } : {})
    }
    if (this.isEdit) {
      this.store.dispatch(new fromShopStore.UpdateShopOrderInvoice({ shopOrderId: this.props.shopOrderId, accountId: this.props.accountId, invoice }))
    } else {
      this.store.dispatch(new fromShopStore.CreateShopOrderInvoice({ shopOrderId: this.props.shopOrderId, invoice }))
    }
  }

  // Voided invoices can be deleted too; only editing them is blocked
  async deleteInvoice() {
    const confirmed = await this.popupService.confirm(
      `Delete ${this.form?.invoice?.docNumber ? 'invoice #' + this.form.invoice.docNumber : 'this invoice'}? It is deleted in QuickBooks too, and its services can be invoiced again.`
    )
    if (confirmed) {
      this.store.dispatch(new fromShopStore.DeleteShopOrderInvoice({ shopOrderId: this.props.shopOrderId, accountId: this.props.accountId }))
    }
  }

  // Unsaved changes are confirmed first
  async close() {
    if (!this.readOnly && this.form && this.state !== this.initialState) {
      if (!(await this.popupService.confirm('Discard your changes to this invoice?'))) {
        return
      }
    }
    this.store.dispatch(new CloseModal('SHOP_INVOICE'))
  }

  ngOnDestroy() {
    this.destroyed$.next(true)
    this.destroyed$.complete()
  }
}
